import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { Card } from '../../../../../../shared/card/card'
import { FleetGrid, useGridList } from '../../../../../../shared/sso-workspace/fleet-grid'
import { Notice } from '../../../../../../shared/sso-workspace/fleet-page'
import { Pill } from '../../../../../../shared/sso-workspace/pill'
import { ConfirmDialog } from '../../../../../../shared/sso-workspace/confirm-dialog'
import { TableAgClient } from '../../../../../../shared/table-builder/table-ag-client'
import { ROW_HEIGHT_TWO_LINE } from '../../../../../../shared/table-builder/table-ag'
import { ColumnFormat, ColumnMenu, ColumnText } from '../../../../../../shared/table-builder/table-columns'
import { Menu, MenuItem } from '../../../../../../shared/menu/menu'
import { IconDelete } from '../../../../../../shared/icon/icon'
import { isCode, serverMessage } from '../../../../../../shared/error/server-message'
import { ErrorReference } from '../../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../../shared/error/use-error-line'
import { dateText, utcText } from '../../../../../../shared/date/local-time'
import { useAuthContext } from '../../../../../../context/auth.context'
import { useRoleOrgUser } from '../../../../../../role/hook/role-org'
import { listLinkedAccounts, revokeLinkedAccount } from '../../../../../../shared/partner/partner-api'
import { linkCreatedBy, linkSourceText, linkStatusWords } from '../../../../../../shared/partner/partner-words'

// A member's linked accounts (Phase E E1a; plan ruling E-5, Task 11): the
// external ids a partner's system (the partner API, later an LMS) knows this
// person by in the organization, each in its system key's id space, with
// its status, where it came from, the key that made it and when. With USER
// update an admin revokes a wrong one, with a reason: the partner API then
// never links that external id to this person again (partner.link_revoked),
// which is the off-ramp for a pre-link that would sign the wrong person in.
//
// The panel shows only when the member has a link: most never will. Before
// the database has linked accounts (content's 503
// linked_account.unavailable) it stays away too. An impersonated session
// reads but never revokes (content refuses it: 403
// auth.impersonation_not_allowed), and says why.

export const READ_ONLY_IMPERSONATING_LINKS = "You're signed in as someone else, so linked accounts can be viewed but not revoked. End the impersonation to make changes."
export const MAX_REASON_LENGTH = 500
export const REASON_RULE = `Give a reason of 1 to ${MAX_REASON_LENGTH} characters.`

const reasonProblem = reason => {
  const n = [...String(reason || '').trim()].length
  return n < 1 || n > MAX_REASON_LENGTH ? REASON_RULE : null
}

// The cells' words, flattened onto the row: the grid sorts, filters and
// exports the field's value, so each column's field holds what it shows.
export const linkRow = link => {
  const words = linkStatusWords(link.status)
  let statusDetail = ''
  if (link.status === 'revoked') statusDetail = [link.revoked_at ? dateText(link.revoked_at) : '', link.revoke_reason || ''].filter(Boolean).join(' · ')
  else if (link.status === 'suspended' && link.suspended_at) statusDetail = `Since ${dateText(link.suspended_at)}`
  return {
    ...link,
    statusText: words.label,
    statusDetail,
    sourceText: linkSourceText(link.source),
    createdByText: linkCreatedBy(link),
    linkedText: link.created_at ? dateText(link.created_at) : '',
    linkedMs: link.created_at ? Date.parse(link.created_at) || 0 : 0
  }
}

// Oldest first, as content answers: by when each was linked, then by id.
const byLinked = (a, b, nodeA, nodeB) => {
  const x = nodeA && nodeA.data ? nodeA.data : {}
  const y = nodeB && nodeB.data ? nodeB.data : {}
  return (x.linkedMs || 0) - (y.linkedMs || 0) || (x.id || 0) - (y.id || 0)
}

const statusCell = cell => {
  const row = cell.data
  if (!row) return ''
  const words = linkStatusWords(row.status)
  return (
    <span className="d-flex flex-column min-w-0">
      <span><Pill tone={words.tone} title={words.title}>{words.label}</Pill></span>
      {row.statusDetail && <span className="sw-cell-sub text-truncate" title={row.statusDetail}>{row.statusDetail}</span>}
    </span>
  )
}
const systemKeyCell = cell => (cell.data ? <span className="sw-mono">{cell.data.system_key}</span> : '')
const linkedCell = cell => (cell.data && cell.data.created_at ? <span title={utcText(cell.data.created_at)}>{cell.data.linkedText}</span> : '')

export const UserLinkedAccounts = ({ userID }) => {
  const ids = { reason: useId() }
  const role = useRoleOrgUser()
  const { state } = useAuthContext()
  const impersonating = !!(state && state.jwtClaims && state.jwtClaims.impersonationEndsAt)
  const canRead = !!role.accessRead
  const canRevoke = !!role.accessUpdate && !impersonating
  const tableRef = useRef()

  const [rows, setRows] = useState(undefined)
  const [hidden, setHidden] = useState(false)
  const [error, failure, setError] = useErrorLine()

  const load = useCallback(async () => {
    try {
      const list = await listLinkedAccounts(userID)
      setError(null)
      setRows(Array.isArray(list) ? list.map(linkRow) : [])
    } catch (err) {
      if (isCode(err, 'linked_account.unavailable')) return setHidden(true)
      setRows(current => (Array.isArray(current) && current.length ? current : null))
      setError(serverMessage(err, "This person's linked accounts couldn't be loaded."), err)
    }
  }, [userID, setError])

  useEffect(() => {
    if (canRead && userID) load()
  }, [canRead, userID, load])

  // the revoke dialog
  const [revoking, setRevoking] = useState(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [actError, actFailure, setActError] = useErrorLine()

  const askRevoke = useCallback(link => {
    setActError(null)
    setReason('')
    setRevoking({ id: link.id, external_id: link.external_id })
  }, [setActError])

  const revoke = async () => {
    if (busy || !revoking) return
    const problem = reasonProblem(reason)
    if (problem) return setActError(problem)
    setBusy(true)
    setActError(null)
    try {
      await revokeLinkedAccount(revoking.id, reason.trim())
      setBusy(false)
      setRevoking(null)
      load()
    } catch (err) {
      setBusy(false)
      // revoked meanwhile (another admin, another tab): the list says so
      if (isCode(err, 'linked_account.not_found')) {
        setRevoking(null)
        return load()
      }
      setActError(serverMessage(err, "The linked account couldn't be revoked."), err)
    }
  }

  const menu = useMemo(() => link => (
    <Menu small>
      <MenuItem Icon={IconDelete} text="Revoke" pin show={canRevoke && link.status !== 'revoked'} onClick={() => askRevoke(link)} />
    </Menu>
  ), [canRevoke, askRevoke])

  const gridList = useGridList(rows)

  if (!canRead || hidden) return null
  // nothing to show until there is a link (or a failure to say so)
  if (!error && (rows === undefined || (Array.isArray(rows) && rows.length === 0))) return null

  return (
    <Card title="Linked accounts">
      <p className="sw-hint small mb-2">
        The external ids a partner's system knows this person by in this organization, each under its system key. Revoking a wrong one means it never links to this person again; their membership and groups stay.
      </p>
      {impersonating && role.accessUpdate && <Notice tone="warn">{READ_ONLY_IMPERSONATING_LINKS}</Notice>}
      {error && <Notice tone="bad" role="alert">{error}<ErrorReference error={failure} /></Notice>}
      {Array.isArray(rows) && rows.length > 0 &&
        <FleetGrid compact flush title="linked accounts" tableRef={tableRef} exportName="linked-accounts" rowHeight={ROW_HEIGHT_TWO_LINE}>
          <TableAgClient ref={tableRef} apiList={gridList} rowHeight={ROW_HEIGHT_TWO_LINE} sortCol="linkedText">
            <ColumnFormat field="system_key" header="System key" render={systemKeyCell} exportValue={link => link.system_key} sortable />
            <ColumnText field="external_id" header="External id" sortable />
            <ColumnFormat field="statusText" header="Status" render={statusCell} exportValue={link => link.statusText} stacked fit="content" sortable />
            <ColumnText field="sourceText" header="Source" sortable />
            <ColumnText field="createdByText" header="Created by" sortable />
            <ColumnFormat field="linkedText" header="Linked" render={linkedCell} comparator={byLinked} exportValue={link => link.created_at} fit="content" sortable />
            <ColumnMenu menu={menu} />
          </TableAgClient>
        </FleetGrid>}

      <ConfirmDialog
        open={Boolean(revoking)}
        title={revoking ? `Revoke the linked account ${revoking.external_id}?` : ''}
        body="The partner API never links this external id to this person again: its next push of it is refused. Their membership and groups stay as they are. This can't be undone."
        confirmText="Revoke linked account"
        busy={busy}
        error={actError}
        failure={actFailure}
        onConfirm={revoke}
        onCancel={() => setRevoking(null)}>
        <div className="mb-3">
          <label className="sw-label" htmlFor={ids.reason}>Reason</label>
          <textarea id={ids.reason} className="form-control" rows={2} maxLength={600} value={reason} onChange={e => setReason(e.target.value)} />
        </div>
      </ConfirmDialog>
    </Card>
  )
}
